import React, { useState, useEffect } from 'react';
import {
    Box,
    Button,
    Paper,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Switch,
    FormControlLabel,
    Alert,
    Snackbar,
    CircularProgress,
    Avatar,
    Chip
} from '@mui/material';
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Close as CloseIcon
} from '@mui/icons-material';
import {
    getCategories,
    createCategory,
    updateCategory,
    deleteCategory
} from '../../../services/firestoreService';
import { uploadImage, deleteImage } from '../../../services/storageService';
import { COLORS } from '../../../assets/colors';

function CategoriesManager() {
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [openDialog, setOpenDialog] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [currentCategory, setCurrentCategory] = useState(null);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [submitting, setSubmitting] = useState(false);

    const [formData, setFormData] = useState({
        name: '',
        image: '',
        imageFile: null,
        active: true,
        isTopCategories: false,
        position: 0
    });

    useEffect(() => {
        loadCategories();
    }, []);

    const loadCategories = async () => {
        setLoading(true);
        const result = await getCategories();
        if (result.success) {
            setCategories(result.data);
        } else {
            showSnackbar('Error al cargar categorías', 'error');
        }
        setLoading(false);
    };

    const handleOpenDialog = (category = null) => {
        if (category) {
            setEditMode(true);
            setCurrentCategory(category);
            setFormData({
                name: category.name,
                image: category.image,
                imageFile: null,
                active: category.active,
                isTopCategories: category.isTopCategories,
                position: category.position
            });
        } else {
            setEditMode(false);
            setCurrentCategory(null);
            setFormData({
                name: '',
                image: '',
                imageFile: null,
                active: true,
                isTopCategories: false,
                position: categories.length
            });
        }
        setOpenDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        setFormData({
            name: '',
            image: '',
            imageFile: null,
            active: true,
            isTopCategories: false,
            position: 0
        });
    };

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            if (!file.type.startsWith('image/')) {
                showSnackbar('Por favor selecciona un archivo de imagen válido', 'error');
                return;
            }
            setFormData({ ...formData, imageFile: file });
        }
    };

    const handleSubmit = async () => {
        if (!formData.name) {
            showSnackbar('El nombre es requerido', 'error');
            return;
        }

        setSubmitting(true);

        try {
            let imageUrl = formData.image;

            // Si hay una nueva imagen, subirla
            if (formData.imageFile) {
                const uploadResult = await uploadImage(
                    formData.imageFile,
                    'RosalStorage/Categories'
                );
                
                if (uploadResult.success) {
                    imageUrl = uploadResult.url;
                    
                    // Si estamos editando y había una imagen anterior, eliminarla
                    if (editMode && formData.image && formData.image !== imageUrl) {
                        await deleteImage(formData.image);
                    }
                } else {
                    showSnackbar('Error al subir la imagen', 'error');
                    setSubmitting(false);
                    return;
                }
            }

            const categoryData = {
                name: formData.name,
                image: imageUrl,
                active: formData.active,
                isTopCategories: formData.isTopCategories,
                position: formData.position
            };

            let result;
            if (editMode) {
                result = await updateCategory(currentCategory.id, categoryData);
            } else {
                result = await createCategory(categoryData);
            }

            if (result.success) {
                showSnackbar(
                    editMode ? 'Categoría actualizada exitosamente' : 'Categoría creada exitosamente',
                    'success'
                );
                handleCloseDialog();
                loadCategories();
            } else {
                showSnackbar('Error al guardar la categoría', 'error');
            }
        } catch (error) {
            showSnackbar('Error inesperado', 'error');
        }

        setSubmitting(false);
    };

    const handleDelete = async (category) => {
        if (window.confirm(`¿Estás seguro de eliminar la categoría "${category.name}"?`)) {
            const result = await deleteCategory(category.id);
            
            if (result.success) {
                // Eliminar la imagen del storage
                if (category.image) {
                    await deleteImage(category.image);
                }
                showSnackbar('Categoría eliminada exitosamente', 'success');
                loadCategories();
            } else {
                showSnackbar('Error al eliminar la categoría', 'error');
            }
        }
    };

    const showSnackbar = (message, severity) => {
        setSnackbar({ open: true, message, severity });
    };

    const handleCloseSnackbar = () => {
        setSnackbar({ ...snackbar, open: false });
    };

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 3 }}>
                <Typography variant="h4" sx={{
                    fontFamily: 'Quicksand',
                    fontWeight: 'bold',
                    color: COLORS.primary
                }}>
                    Gestión de Categorías
                </Typography>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => handleOpenDialog()}
                    sx={{
                        background: `linear-gradient(135deg, ${COLORS.primary} 0%, rgba(30, 58, 95, 0.95) 100%)`,
                        fontFamily: 'Quicksand',
                        fontWeight: 'bold',
                        textTransform: 'none',
                        borderRadius: '30px',
                        padding: '10px 24px'
                    }}
                >
                    Nueva Categoría
                </Button>
            </Box>

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', padding: 4 }}>
                    <CircularProgress />
                </Box>
            ) : (
                <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
                    <Table>
                        <TableHead sx={{ background: COLORS.primary }}>
                            <TableRow>
                                <TableCell sx={{ color: 'white', fontFamily: 'Quicksand', fontWeight: 'bold' }}>
                                    Imagen
                                </TableCell>
                                <TableCell sx={{ color: 'white', fontFamily: 'Quicksand', fontWeight: 'bold' }}>
                                    Nombre
                                </TableCell>
                                <TableCell sx={{ color: 'white', fontFamily: 'Quicksand', fontWeight: 'bold' }}>
                                    Posición
                                </TableCell>
                                <TableCell sx={{ color: 'white', fontFamily: 'Quicksand', fontWeight: 'bold' }}>
                                    Estado
                                </TableCell>
                                <TableCell sx={{ color: 'white', fontFamily: 'Quicksand', fontWeight: 'bold' }}>
                                    Top Categorías
                                </TableCell>
                                <TableCell sx={{ color: 'white', fontFamily: 'Quicksand', fontWeight: 'bold' }} align="right">
                                    Acciones
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {categories.map((category) => (
                                <TableRow key={category.id} hover>
                                    <TableCell>
                                        <Avatar
                                            src={category.image}
                                            alt={category.name}
                                            sx={{ width: 60, height: 60, borderRadius: 2 }}
                                        />
                                    </TableCell>
                                    <TableCell sx={{ fontFamily: 'Quicksand', fontWeight: 600 }}>
                                        {category.name}
                                    </TableCell>
                                    <TableCell sx={{ fontFamily: 'Quicksand' }}>
                                        {category.position}
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            label={category.active ? 'Activo' : 'Inactivo'}
                                            color={category.active ? 'success' : 'default'}
                                            size="small"
                                            sx={{ fontFamily: 'Quicksand', fontWeight: 600 }}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            label={category.isTopCategories ? 'Sí' : 'No'}
                                            color={category.isTopCategories ? 'primary' : 'default'}
                                            size="small"
                                            sx={{ fontFamily: 'Quicksand', fontWeight: 600 }}
                                        />
                                    </TableCell>
                                    <TableCell align="right">
                                        <IconButton
                                            onClick={() => handleOpenDialog(category)}
                                            sx={{ color: COLORS.secondary }}
                                        >
                                            <EditIcon />
                                        </IconButton>
                                        <IconButton
                                            onClick={() => handleDelete(category)}
                                            sx={{ color: '#dc3545' }}
                                        >
                                            <DeleteIcon />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {/* Dialog para Crear/Editar */}
            <Dialog
                open={openDialog}
                onClose={handleCloseDialog}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle sx={{
                    fontFamily: 'Quicksand',
                    fontWeight: 'bold',
                    color: COLORS.primary,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                }}>
                    {editMode ? 'Editar Categoría' : 'Nueva Categoría'}
                    <IconButton onClick={handleCloseDialog}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ paddingTop: 2 }}>
                        <TextField
                            fullWidth
                            label="Nombre de la Categoría"
                            value={formData.name}
                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                            sx={{ marginBottom: 2 }}
                            InputLabelProps={{ sx: { fontFamily: 'Quicksand' } }}
                            InputProps={{ sx: { fontFamily: 'Quicksand' } }}
                        />

                        <TextField
                            fullWidth
                            type="number"
                            label="Posición"
                            value={formData.position}
                            onChange={(e) => setFormData({ ...formData, position: parseInt(e.target.value) })}
                            sx={{ marginBottom: 2 }}
                            InputLabelProps={{ sx: { fontFamily: 'Quicksand' } }}
                            InputProps={{ sx: { fontFamily: 'Quicksand' } }}
                        />

                        <Button
                            variant="outlined"
                            component="label"
                            fullWidth
                            sx={{
                                marginBottom: 2,
                                fontFamily: 'Quicksand',
                                textTransform: 'none',
                                borderColor: COLORS.secondary,
                                color: COLORS.secondary,
                                '&:hover': {
                                    borderColor: COLORS.secondary,
                                    background: `${COLORS.secondary}10`
                                }
                            }}
                        >
                            {formData.imageFile ? formData.imageFile.name : 'Seleccionar Imagen'}
                            <input
                                type="file"
                                hidden
                                accept="image/*"
                                onChange={handleImageChange}
                            />
                        </Button>

                        {(formData.image || formData.imageFile) && (
                            <Box sx={{
                                marginBottom: 2,
                                display: 'flex',
                                justifyContent: 'center'
                            }}>
                                <Avatar
                                    src={formData.imageFile ? URL.createObjectURL(formData.imageFile) : formData.image}
                                    alt="Preview"
                                    sx={{ width: 120, height: 120, borderRadius: 2 }}
                                />
                            </Box>
                        )}

                        <FormControlLabel
                            control={
                                <Switch
                                    checked={formData.active}
                                    onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                                />
                            }
                            label="Activo"
                            sx={{
                                marginBottom: 1,
                                '& .MuiTypography-root': { fontFamily: 'Quicksand', fontWeight: 600 }
                            }}
                        />

                        <FormControlLabel
                            control={
                                <Switch
                                    checked={formData.isTopCategories}
                                    onChange={(e) => setFormData({ ...formData, isTopCategories: e.target.checked })}
                                />
                            }
                            label="Top Categorías"
                            sx={{
                                '& .MuiTypography-root': { fontFamily: 'Quicksand', fontWeight: 600 }
                            }}
                        />
                    </Box>
                </DialogContent>
                <DialogActions sx={{ padding: 3 }}>
                    <Button
                        onClick={handleCloseDialog}
                        sx={{
                            fontFamily: 'Quicksand',
                            fontWeight: 'bold',
                            textTransform: 'none',
                            color: '#666'
                        }}
                    >
                        Cancelar
                    </Button>
                    <Button
                        onClick={handleSubmit}
                        variant="contained"
                        disabled={submitting}
                        sx={{
                            background: `linear-gradient(135deg, ${COLORS.primary} 0%, rgba(30, 58, 95, 0.95) 100%)`,
                            fontFamily: 'Quicksand',
                            fontWeight: 'bold',
                            textTransform: 'none'
                        }}
                    >
                        {submitting ? 'Guardando...' : (editMode ? 'Actualizar' : 'Crear')}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Snackbar para notificaciones */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert
                    onClose={handleCloseSnackbar}
                    severity={snackbar.severity}
                    sx={{ fontFamily: 'Quicksand', fontWeight: 600 }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}

export default CategoriesManager;
